<template>
  <section>
    <collapsable
      :collapsed="collapsed"
      :title=" `${$t('entities.entity_aud_provider_forwarding_rules.title')} (${entity.aud_provider.name})`"
    >
      <section class="facility-list px-1">
        <div v-if="rules.length && !isLoading">
          <div v-for="rule in rules" class="rule-card">

            <div class="row">
              <span class="is-bold">{{ $t('users.inputs.name') }}: </span>
              &nbsp;
              <span> {{ rule.name }} </span>
            </div>
            <div class="row d-flex align-items-center justify--space-between"
                 v-if="rule.criterias && rule.criterias.length > 0">
              <div>
                <span class="is-bold">{{ $t('entities.entity_aud_provider_forwarding_rules.criterias_title') }}: </span>
                &nbsp;
                <span> {{ rule.criterias.length }} </span>
              </div>
              <i class="fa fa-chevron-down clickable" v-if="!rule.criteriasExpanded"
                 @click="rule.criteriasExpanded = true" />
              <i class="fa fa-chevron-up clickable" v-else @click="rule.criteriasExpanded = false" />
            </div>
            <div class="row" v-if="rule.criteriasExpanded">
              <div class="mt-2 ml-3 mr-3 w-100">
                <h5 class="is-bold"> {{ $t('entities.entity_aud_provider_forwarding_rules.criterias_list') }} </h5>
                <table>
                  <thead>
                  <tr>
                    <th> {{$t('entities.entity_aud_provider_forwarding_rules.criterias.lead_field')}}</th>
                    <th> {{ $t('entities.entity_aud_provider_forwarding_rules.criterias.comparator') }}</th>
                    <th> {{ $t('entities.entity_aud_provider_forwarding_rules.criterias.value') }}</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="crit of rule.criterias">
                    <td> {{ crit.lead_field }}</td>
                    <td> {{ crit.comparator }}</td>
                    <td> {{ crit.value ? crit.value : 'N/A' }}</td>
                  </tr>
                  </tbody>

                </table>
                <div class="d-flex justify--space-between px-3 mt-2">
                  <va-button v-if="hasWritePermissionsToEntity"
                             @click.prevent="showDeleteRuleConfirmation(rule)" class="del-rule-btn">
                    <span class="is-bold"> {{$t('entities.entity_aud_provider_forwarding_rules.delete_rule')}} </span>
                  </va-button>
                  <va-button v-if="hasWritePermissionsToEntity"
                             @click.prevent="addCriteria(rule)" class="add-crit-btn">
                    <span class="is-bold"> {{$t('entities.entity_aud_provider_forwarding_rules.add_criteria')}} </span>
                  </va-button>
                </div>
              </div>

            </div>

          </div>
        </div>

        <div v-else class="mt-2 ml-3 mb-5">
          <span class="is-bold">{{ $t('entities.entity_aud_provider_forwarding_rules.empty_list') }}</span>
        </div>

        <div class="d-flex justify-content-end px-3">

          <va-button v-if="hasWritePermissionsToEntity"
                     @click.prevent="onCreateRule">
            <span class="is-bold"> {{ $t('entities.entity_aud_provider_forwarding_rules.new_rule') }} </span>
          </va-button>
        </div>
      </section>
    </collapsable>

    <CreateNewRuleModal
      v-if="showCreateNewRuleModal"
      :audProviderId="this.entity.aud_provider_id"
      :entityId="this.entity.id"
      @canceled="onCancelCreateRule"
      @created="ruleCreated"
    />

    <AddCriteriaModal
      v-if="showAddCriteriaModal"
      :rule="selectedRule"
      @canceled="onCancelAddCriteria"
      @created="criteriaAdded"
    />


    <ConfirmationModal
      v-if="isDeletingRule"
      :title="$t('entities.entity_aud_provider_forwarding_rules.delete_rule')"
      :body="$t('entities.entity_aud_provider_forwarding_rules.delete_rule_confirm')"
      @modal-confirmed="confirmDeleteRule"
      @modal-canceled="cancelDeleteRule" />

  </section>

</template>

<script>
import { FacilityService, FacilityScheduleService } from '@/api/AuthService';
import Collapsable from '@/components/common/Collapsable.vue';
import FacilityListItem from '@/components/entities/facilities/FacilityListItem.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import { LeadsService } from '@/api/LeadsService';
import CreateNewRuleModal from '@/components/entities/EntityAudProviderForwardingRules/CreateNewRuleModal.vue';
import AddCriteriaModal from '@/components/entities/EntityAudProviderForwardingRules/AddCriteriaModal.vue';

const entityInfoType = 110;
const writeAccessType = 2;

export default {
  props: {
    entity: {
      type: Object,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: false,
    },
  },
  components: {
    AddCriteriaModal,
    CreateNewRuleModal,
    Collapsable,
    FacilityListItem,
    ConfirmationModal,
  },
  computed: {
    hasWritePermissionsToEntity: function() {
      return this.authService && this.authService.user_permissions.some(p => (p.entity_id === null || p.entity_id === this.entity.id) && p.info_type === entityInfoType && p.access_type === writeAccessType);
    },
    authService: function() {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollauth_server) || null;
    },
  },
  data() {
    return {
      isLoading: true,
      rules: [],
      showCreateNewRuleModal: false,
      isDeletingRule: false,
      ruleToDelete: null,
      selectedRule: null,
      showAddCriteriaModal: false
    };
  },
  methods: {
    loadRules() {
      this.isLoading = true;
      LeadsService.getAudProviderForwardingRules(this.entity.aud_provider_id, this.entity.id)
        .then(rules => {
          rules.forEach(rule => rule.criteriasExpanded = false);
          this.rules = rules;
        })
        .catch(errors => {
          console.log(errors);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    onCreateRule() {
      this.showCreateNewRuleModal = true
    },
    onCancelCreateRule() {
      this.showCreateNewRuleModal = false
    },
    ruleCreated(data) {
      data.criteriasExpanded = false
      this.rules.push(data)
      this.showCreateNewRuleModal = false
    },
    showDeleteRuleConfirmation(rule) {
      this.ruleToDelete = rule
      this.isDeletingRule = true
    },
    async confirmDeleteRule() {
      try {
        await LeadsService.deleteRule(this.ruleToDelete.id)
        const idx = this.rules.findIndex(rule => rule.id === this.ruleToDelete.id)
        this.rules.splice(idx, 1)
      } catch (error) {
        console.log(error)
      }

      this.ruleToDelete = null
      this.isDeletingRule = false
    },
    cancelDeleteRule() {
      this.ruleToDelete = null
      this.isDeletingRule = false
    },
    addCriteria(rule) {
      this.selectedRule = rule
      this.showAddCriteriaModal = true
    },
    onCancelAddCriteria() {
      this.selectedRule = null
      this.showAddCriteriaModal = false
    },
    criteriaAdded(data) {
      this.selectedRule.criterias.push(data)
      this.selectedRule = null
      this.showAddCriteriaModal = false
    }
  },
  watch: {
    entity: {
      immediate: true,
      handler: function() {
        this.loadRules();
      },
    },
  },
};
</script>

<style lang="scss" scoped>

.rule-card {
  border: solid 1px #ededed;
  background-color: #ededed;
  box-shadow: 8px 8px 8px #e0e0e0;
  border-radius: 15px;
  padding: 15px;
  margin-bottom: 10px;
  margin-top: 15px;
}

.row:not(:only-child) {
  margin: unset !important;
}

table {
  width: 100%;
  border-collapse: collapse;
  overflow: hidden;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.1);
  border-radius: 15px;
}

th,
td {
  padding: 15px;
  background-color: rgba(183, 223, 198, 0.55);
  color: #464646;
}

th {
  text-align: center;
  font-weight: bold;
}

thead {
  th {
    background-color: #79c09c;
  }
}

tbody {
  tr {
    &:hover {
      background-color: rgba(255, 255, 255, 0.3);
    }
  }

  td {
    position: relative;
    text-align: center;

    &:hover {
      &:before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: -9999px;
        bottom: -9999px;
        background-color: rgba(255, 255, 255, 0.2);
        z-index: -1;
      }
    }
  }
}

.add-crit-btn {
  background-color: #79c09c !important;
}
.del-rule-btn {
  background-color: #bc5b5b !important;
}

</style>
